import { Draft } from 'immer';
import {
  CellErrors,
  CellPosition,
  CellValue,
  Cells,
  EntityIdToName,
  ReferenceType,
  Sheet,
} from '../../types';
import { SheetManagementSet } from '../../types/sheet/draft';
import { createSheetDraft } from '../sheet/updates';
import { QueueSet, Queue, QueueMap, QueueItemType } from './queue';
import { queueIndependencies } from './independencies';
import { isCellPosition } from '../address/parsingAddress';
import { createCell } from '../cells/cellCRUD';
import { validateFormulaForCell } from '../address/formulaValidation';
import { extractFormulaAddresses } from '../address/formulaAddress';
import FormulaParser from '../formula/parserSingleton';

export interface CellDependency {
  type: ReferenceType.CellFormula;
  cellPosition: CellPosition;
  cellAddress: string;
  calculateSelf: boolean;
}

export interface DataValidationDependency {
  type: ReferenceType.DataValidation;
  id: string;
}

export interface ConditionalFormatDependency {
  type: ReferenceType.ConditionalFormat;
  id: string;
}

export interface DynamicFormulaDependency {
  type: ReferenceType.DynamicFormula;
  id: string;
}

export type ComputeIndepsDependency =
  | CellDependency
  | DataValidationDependency
  | ConditionalFormatDependency
  | DynamicFormulaDependency;

// #region start computing indeps
export async function startComputingIndeps(
  set: SheetManagementSet,
  depSheetId: string,
  depedency: ComputeIndepsDependency,
  updateNumberFormatOnly = false
) {
  const queueSet: QueueSet = {
    visited: new Map(),
    waiting: new Map(),
    queue: new Queue(),
    validationsQueue: [],
    conditionalFormatsQueue: [],
  };

  if (depedency.type === ReferenceType.CellFormula) {
    await computeCellIndependencies(
      set,
      queueSet,
      depSheetId,
      depedency,
      updateNumberFormatOnly
    );
  }
}

async function computeCellIndependencies(
  set: SheetManagementSet,
  queueSet: QueueSet,
  depSheetId: string,
  depedency: CellDependency,
  updateNumberFormatOnly: boolean
) {
  const { calculateSelf, cellAddress, cellPosition } = depedency;

  if (calculateSelf) {
    queueSet.queue.enqueue({
      type: QueueItemType.Cell,
      sheetId: depSheetId,
      address: cellAddress,
      position: cellPosition,
    });
  } else {
    await queueIndependencies(
      set,
      queueSet,
      cellPosition,
      depSheetId,
      cellPosition
    );
  }
  const sheetsIdToName = set.staticEntityIdToNameMap.sheetsIdToName;

  while (!queueSet.queue.isEmpty()) {
    const extracted = queueSet.queue.dequeue();
    if (extracted) {
      const error = queueSet.circularError;

      //```` ERROR CASE `````
      if (extracted.type === QueueItemType.Error) {
        if (!updateNumberFormatOnly && error)
          await addErrorToVisitedCells(set, queueSet.visited, error);

        continue;
      }
      //```` ERROR CASE `````

      const { sheetId, address, position } = extracted;

      if (queueSet.visited.get(sheetId)?.has(address)) continue;
      if (!sheetsIdToName[sheetId]) continue;

      const sheet = await createSheetDraft(sheetId, set);

      await computeResult(set, sheet, position, error, updateNumberFormatOnly);

      if (!queueSet.visited.get(sheetId))
        queueSet.visited.set(sheetId, new Set());

      queueSet.visited.get(sheetId)?.add(address);
      queueSet.waiting.get(sheetId)?.delete(address);

      await queueIndependencies(set, queueSet, cellPosition, sheetId, position);
    }
  }
}

async function addErrorToVisitedCells(
  set: SheetManagementSet,
  visited: QueueMap,
  error: string
) {
  for (const sheetId of visited.keys()) {
    const sheetVisited = visited.get(sheetId) ?? [];
    for (const address of sheetVisited) {
      const isCell = isCellPosition(address);
      if (!isCell) continue;

      const sheet = await createSheetDraft(sheetId, set);

      await computeResult(set, sheet, isCell.position, error);
    }
  }
}
// #endregion

// #region compute result
async function computeResult(
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  position: CellPosition,
  err?: string,
  updateNumberFormatOnly?: boolean,
  dynamicFormula?: string
) {
  const { id: sheetId, cells, maxColIndex, maxRowIndex } = sheet;
  const sheetsIdToName = set.staticEntityIdToNameMap.sheetsIdToName;
  const cell = createCell(sheet.cells, position);

  let formula = dynamicFormula ?? cell.v?.toString();
  if (!formula || !formula.startsWith('=')) return;

  const error =
    err ??
    validateCellFormula(
      position,
      formula,
      sheetsIdToName,
      maxColIndex,
      maxRowIndex
    );

  if (error) {
    saveResult(cells, position, error);
    return;
  }

  formula = formula.replace('=', '');

  await processFormulaMentions(set, sheetId, formula, updateNumberFormatOnly);

  try {
    const limitedSet = {
      temporarySheets: set.temporarySheets,
      draftedSheets: set.draftedSheets,
    };

    const cellInfo = {
      sheet: sheetId,
      col: position.ci,
      row: position.ri,
    };

    let result = await FormulaParser.parse(formula, limitedSet, cellInfo);
    if (typeof result === 'object' && result) {
      result = result._error;
    }
    saveResult(cells, position, result);
  } catch (err) {
    console.log('err', err);
    const ERROR = getCellErrorType(JSON.stringify(err));
    saveResult(cells, position, ERROR);
  }
}
// #endregion

// #region Helpers
const saveResult = (
  cells: Cells,
  position: CellPosition,
  result: CellValue
) => {
  const cell = createCell(cells, position);
  cell.r = result;
};

const getCellErrorType = (e: string) => {
  if (e.includes('#REF')) return CellErrors.REF;

  return CellErrors.ERROR;
};

const processFormulaMentions = async (
  set: SheetManagementSet,
  parentSheetId: string,
  formula: string,
  updateNumberFormatOnly?: boolean
) => {
  const sheetIdToName = set.staticEntityIdToNameMap.sheetsIdToName;
  const addresses = extractFormulaAddresses(formula, parentSheetId);

  // deleteCellDepNumberFormat(draft.depNumberFormats, colId, rowId);

  for (const { sheetId, address } of addresses) {
    if (!sheetIdToName[sheetId]) continue;
    // if (sheetId === parentSheetId) continue; // Sheet already loaded

    const isCell = isCellPosition(address);
    if (isCell) {
      const sheet = await createSheetDraft(sheetId, set);
      // TODO: Handle Dependent Number Formats
    } else if (!updateNumberFormatOnly) {
      // Do not create draft Only Load Sheet From DB
      await createSheetDraft(sheetId, set, true);
    }
  }
};

const validateCellFormula = (
  cellPos: CellPosition,
  value: string,
  sheetsIdToName: EntityIdToName,
  maxColIndex: number,
  maxRowIndex: number
): string | undefined => {
  if (value.startsWith('=')) {
    return validateFormulaForCell(
      cellPos,
      value,
      sheetsIdToName,
      maxColIndex,
      maxRowIndex
    );
  }
};
// #endregion
